import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthTokenContext } from '@/auth/KeycloakProvider';
import { config } from '@/config';
import { ROUTES } from '@/config/routes';

/**
 * Result from access link validation API
 */
interface AccessLinkResult {
  id: string;
  purpose: string;
  validUntil: string;
  masterClassId: string;
}

/**
 * Validates an access code via REST API.
 * Uses fetch (not Apollo) as Apollo Client is configured in Phase 3.
 */
const validateAccessCode = async (
  code: string,
  accessToken?: string
): Promise<AccessLinkResult> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${config.BACKEND_URL}/access-links/validate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as { message?: string }).message || 'Invalid access link');
  }

  return response.json() as Promise<AccessLinkResult>;
};

/**
 * Hook for handling access link validation and guest account creation.
 *
 * Flow:
 * 1. Validate code FIRST (before any account creation)
 * 2. If code is valid but user not authenticated, create guest account
 * 3. Re-validate with new auth to grant access
 * 4. Navigate to content
 *
 * This prevents creating orphan guest accounts for invalid codes.
 */
export const useAccessLink = () => {
  const { tokenStatus, askForGuestAccess, getFreshToken } = useAuthTokenContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handleAccessLink = async (code: string) => {
    setLoading(true);
    setError(null);

    try {
      // STEP 1: Validate code FIRST (before any account creation)
      // If user is already authenticated, include their token
      let accessToken: string | undefined;
      if (tokenStatus === 'valid') {
        const token = await getFreshToken();
        accessToken = token ?? undefined;
      }

      // Try validation - this will tell us if code is valid
      // For unauthenticated users, backend may return 401 requiring auth
      const validationResult = await validateAccessCode(code, accessToken);

      // If we got here with valid tokenStatus, we're done
      if (tokenStatus === 'valid') {
        navigate(ROUTES.MASTERCLASS_HOME);
        return validationResult;
      }

      // STEP 2: Code is valid but user not authenticated
      // NOW create guest account (code already validated)
      // Generate a guest username based on timestamp for uniqueness
      const guestUsername = `guest-${Date.now()}`;
      await askForGuestAccess({ code, username: guestUsername });

      // STEP 3: Re-validate with new auth to grant access
      const newToken = await getFreshToken();
      await validateAccessCode(code, newToken ?? undefined);

      // STEP 4: Navigate to content
      navigate(ROUTES.MASTERCLASS_HOME);
      return validationResult;
    } catch (err) {
      // Handle case where validation fails for unauthenticated user
      // This is expected - we need to create guest account first in this case
      if (err instanceof Error && err.message.includes('401')) {
        // Backend requires auth to validate - create guest then retry
        try {
          const guestUsername = `guest-${Date.now()}`;
          await askForGuestAccess({ code, username: guestUsername });
          const newToken = await getFreshToken();
          const result = await validateAccessCode(code, newToken ?? undefined);
          navigate(ROUTES.MASTERCLASS_HOME);
          return result;
        } catch (guestErr) {
          setError(
            guestErr instanceof Error ? guestErr : new Error('Failed to create guest account')
          );
          throw guestErr;
        }
      }

      setError(err instanceof Error ? err : new Error('Invalid access link'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleAccessLink, loading, error };
};
